<template>
  <listing-layout>
    <div slot="heading" class="d-flex justify-content-between align-items-center mb-3">
      <h1 class="h2 mb-0">Judge Dashboard</h1>
    </div>
    <div slot="content">
      <div v-if="memberDetails">
        <div v-if="!checkHasFinancialMembers">
          Please pay ACA membership to apply for a Judge position.
        </div>
        <div v-if="checkHasFinancialMembers">
          <router-link tag="button" :to="{ name: 'Judge Application' }"
                       class="btn btn-block mb-2 btn-primary button-link">Add a Trial Judge
          </router-link>

          <div class="alert alert-success" v-if="success">{{ success }}</div>
          <div class="alert alert-warning" v-if="error">{{ error }}</div>
          <data-table :dataRows="rows" :dataColumns="columns" :options="options">
            <tableTextTemplate slot-scope="{ row }" slot="expandedHeader" :text="row.first_name + ' ' + row.last_name" :slug="row.id" :row="row" />
            <div slot-scope="{ row }" slot="expandedRow">
              <ul class="list-unstyled mb-0 pt-2">
                <li>
                  <strong>First Name: </strong>
                  <tableTextTemplate :text="row.first_name" :row="row" :options="{ inline: true }"/>
                </li>
                <li>
                  <strong>Last Name: </strong>
                  <tableTextTemplate :text="row.last_name" :row="row" :options="{ inline: true }"/>
                </li>
                <li>
                  <strong>Judge Status: </strong>
                  <tableTextTemplate :text="row.judge_type" :row="row" :options="{ inline: true }"/>
                </li>
                <li>
                  <strong>State: </strong>
                  <tableTextTemplate :row="row" :options="{ inline: true, showJudgeStatus: true }"/>
                </li>
                <li class="mt-2 d-flex flex-wrap">
                  <div class="mr-2">
                    <router-link
                      :to="{ name: 'Active Drafts', params: { slug: row.judge_id } }"
                      tag="button" class="btn btn-block mb-2 btn-outline-primary btn-sm"
                      :class="!row.judge_id ? 'disabled' : ''"
                      :disabled="!row.judge_id"
                      style="width: 170px;">
                      View Drafts
                    </router-link>
                  </div>
                  <div class="mr-2">
                    <router-link
                      :to="{ name: 'Expression Of Interest', params: { slug: row.judge_id } }"
                      tag="button" class="btn btn-block mb-2 btn-primary btn-sm"
                      :class="!row.judge_id ? 'disabled' : ''"
                      :disabled="!row.judge_id"
                      style="width: 170px;">
                      Express Interest
                    </router-link>
                  </div>
                  <div class="mr-2">
                    <router-link
                      :to="{ name: 'Judge Application', params: { member_id: row.user_id} }"
                      tag="button" class="btn btn-block mb-2 btn-primary btn-sm" style="width: 170px;"
                      v-if="(row.judge_type === 'trial')">
                      Apply for Open
                    </router-link>
                  </div>
                </li>
              </ul>
            </div>
          </data-table>
        </div>
      </div>
      <div class="bg-white rounded mb-4 p-3 p-sm-4 flex-grow-1 mr-lg-3" v-else>
        <div class="text-center p-2">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    </div>
  </listing-layout>
</template>

<script>
/* Use the "slot" attribute to match the heading and content
 * to their respective fields in the ListingLayout component. */
import GETSTORE from '@/store/memberProfileStore.js'
import axios from 'axios'
import listingLayout from '@/components/ListingLayout'
import dataTable from '@/components/DataTable'
import tableTextTemplate from '@/components/DataTableFields/Text'

export default {
  name: 'JudgeDashboard',
  props: ['success'],
  mixins: [GETSTORE],
  components: {
    'listing-layout': listingLayout,
    'data-table': dataTable,
    tableTextTemplate
  },
  data() {
    return {
      userID: localStorage.userID,
      userRole: localStorage.role,
      loggedInUser: localStorage.role,
      status: '',
      error: '',
      rows: [],
      unregisterExist: false,
      columns: [
        {
          label: 'First Name',
          sortable: false,
          headerClass: 'd-none d-lg-table-cell judge-dashboard-name',
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `
              <tableTextTemplate :text="row.first_name" :slug="row.id" :row="row"/>`
          })
        },
        {
          label: 'Last Name',
          sortable: false,
          headerClass: 'd-none d-lg-table-cell judge-dashboard-name',
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `
              <tableTextTemplate :text="row.last_name" :row="row"/>`
          })
        },
        {
          label: 'Judge Status',
          sortable: false,
          headerClass: 'd-none d-lg-table-cell judge-dashboard-status',
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `
              <tableTextTemplate :text="_.capitalize(row.judge_type)" :row="row"/>`
          })
        },
        {
          label: 'State',
          filterable: false,
          sortable: false,
          headerClass: 'd-none d-lg-table-cell',
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<span :class="['badge ml-1 my-auto', row.status === 'denied' ? 'alert-danger' : (row.status === 'approved' ? 'alert-success' : 'alert-warning')]">{{ _.capitalize(row.status) }}</span>`
          })
        },
        {
          label: 'Active Drafts',
          sortable: false,
          headerClass: 'd-none d-lg-table-cell judge-dashboard-actions',
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `
              <router-link
                :to="{ name: 'Active Drafts', params: { slug: row.judge_id } }"
                tag="button" class="btn btn-block mb-2 btn-outline-primary btn-sm"
                :class="!row.judge_id ? 'disabled' : ''"
                :disabled="!row.judge_id"
                style="width: 170px">
                View Drafts
              </router-link>`
          })
        },
        {
          label: 'Actions',
          sortable: false,
          headerClass: 'd-none d-lg-table-cell judge-dashboard-actions',
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `
              <router-link
                :to="{ name: 'Expression Of Interest', params: { slug: row.judge_id } }"
                tag="button" class="btn btn-block mb-2 btn-primary btn-sm"
                :class="!row.judge_id ? 'disabled' : ''"
                :disabled="!row.judge_id"
                style="width: 170px">
                Express Interest
              </router-link>`
          })
        },
        {
          label: '',
          sortable: false,
          headerClass: 'd-none d-lg-table-cell judge-dashboard-actions',
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `
              <router-link
                :to="{ name: 'Judge Application', params: { member_id: row.user_id} }"
                tag="button" class="btn btn-block mb-2 btn-primary btn-sm" style="width: 170px"
                v-if="(row.judge_type === 'trial')">
                Apply for Open
              </router-link>`
          })
        }
      ],
      options: {
        filter: {},
        activeFilter: {
          registration_date: 'all'
        },
        search: false,
        addHorse: false,
        loaded: false
      }
    }
  },
  computed: {
    checkHasFinancialMembers() {
      return !!this.memberDetails.secondaries.find(member => member.financial && !member.has_event_membership) ||
        (this.memberDetails.financial && !this.memberDetails.has_event_membership)
    }
  },
  mounted() {
    this.$store.commit('deselectAllRows')
    this.getJudgeList()
    if (this.horseError) {
      this.error = this.horseError
    }
  },
  methods: {
    getJudgeList() {
      axios.get(`${this.$api}/judges/dashboard`, {
        id: localStorage.userID
      })
        .then((response) => {
          if (response.data.status === 'success') {
            this.error = this.horseError || ''
            this.rows = response.data.data
            this.options.loaded = true
          }
        })
        .catch((error) => {
          // eslint-disable-next-line
          console.log(error)
        })
    }
  }
}
</script>
<style scoped>
.button-link {
  width: 210px;
}
</style>
<style>
.judge-dashboard-actions {
  width: 215px;
}

.judge-dashboard-name {
  width: 300px;
}

.judge-dashboard-status {
  width: 180px;
}
</style>



// WEBPACK FOOTER //
// src/views/Judges/Dashboard/JudgeDashboard.vue